import React from 'react'
import { cn } from '../../utils/cn'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'outline' | 'solid'
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary'
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
  rounded?: 'full' | 'lg'
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(({
  variant = 'default',
  color = 'primary',
  size = 'md',
  dot = false,
  rounded = 'full',
  className,
  children,
  ...props
}, ref) => {
  const baseStyles = cn(
    'inline-flex items-center gap-1.5 font-medium',
    'transition-colors duration-200',
    rounded === 'full' && 'rounded-full',
    rounded === 'lg' && 'rounded-lg',
    className
  )

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  }

  const colorStyles = {
    primary: {
      default: 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
      solid: 'bg-primary-500 text-white',
      outline: 'bg-transparent border border-primary-500 text-primary-700 dark:text-primary-400',
    },
    success: {
      default: 'bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-400',
      solid: 'bg-success-500 text-white',
      outline: 'bg-transparent border border-success-500 text-success-700 dark:text-success-400',
    },
    warning: {
      default: 'bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400',
      solid: 'bg-warning-500 text-white',
      outline: 'bg-transparent border border-warning-500 text-warning-700 dark:text-warning-400',
    },
    error: {
      default: 'bg-error-50 dark:bg-error-900/30 text-error-700 dark:text-error-400',
      solid: 'bg-error-500 text-white',
      outline: 'bg-transparent border border-error-500 text-error-700 dark:text-error-400',
    },
    info: {
      default: 'bg-info-50 dark:bg-info-900/30 text-info-700 dark:text-info-400',
      solid: 'bg-info-500 text-white',
      outline: 'bg-transparent border border-info-500 text-info-700 dark:text-info-400',
    },
    secondary: {
      default: 'bg-secondary-50 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-400',
      solid: 'bg-secondary-500 text-white',
      outline: 'bg-transparent border border-secondary-500 text-secondary-700 dark:text-secondary-400',
    },
  }

  const dotColors = {
    primary: 'bg-primary-500',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error: 'bg-error-500',
    info: 'bg-info-500',
    secondary: 'bg-secondary-500',
  }

  const dotSize = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  }

  return (
    <span
      ref={ref}
      className={cn(
        baseStyles,
        sizeStyles[size],
        colorStyles[color][variant]
      )}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            dotColors[color],
            dotSize[size],
            'rounded-full flex-shrink-0'
          )}
        />
      )}
      {children}
    </span>
  )
})

Badge.displayName = 'Badge'

// Status Badge - For showing status indicators
interface StatusBadgeProps extends Omit<BadgeProps, 'color' | 'children'> {
  status: 'active' | 'inactive' | 'pending' | 'success' | 'warning' | 'error' | 'failed' | 'unsupported' | 'info' | string
  label?: string
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(({
  status,
  label,
  size = 'md',
  dot = true,
  ...props
}, ref) => {
  const statusConfig = {
    active: {
      label: label || 'Active',
      color: 'success' as const,
    },
    inactive: {
      label: label || 'Inactive',
      color: 'secondary' as const,
    },
    pending: {
      label: label || 'Pending',
      color: 'warning' as const,
    },
    success: {
      label: label || 'Success',
      color: 'success' as const,
    },
    warning: {
      label: label || 'Warning',
      color: 'warning' as const,
    },
    error: {
      label: label || 'Error',
      color: 'error' as const,
    },
    failed: {
      label: label || 'Failed',
      color: 'error' as const,
    },
    unsupported: {
      label: label || 'Unsupported',
      color: 'warning' as const,
    },
    info: {
      label: label || 'Info',
      color: 'info' as const,
    },
  }

  const normalizedStatus = String(status || 'info').toLowerCase()
  const config = statusConfig[normalizedStatus as keyof typeof statusConfig] || {
    label: label || normalizedStatus,
    color: 'secondary' as const,
  }

  return (
    <Badge
      ref={ref}
      color={config.color}
      size={size}
      dot={dot}
      {...props}
    >
      {config.label}
    </Badge>
  )
})

StatusBadge.displayName = 'StatusBadge'

// Count Badge - For showing counts
interface CountBadgeProps extends Omit<BadgeProps, 'children'> {
  count: number
  max?: number
  showZero?: boolean
}

export const CountBadge = React.forwardRef<HTMLSpanElement, CountBadgeProps>(({
  count,
  max = 99,
  showZero = false,
  size = 'sm',
  color = 'error',
  variant = 'solid',
  ...props
}, ref) => {
  if (count === 0 && !showZero) {
    return null
  }

  const displayCount = count > max ? `${max}+` : count.toString()

  return (
    <Badge
      ref={ref}
      color={color}
      variant={variant}
      size={size}
      {...props}
    >
      {displayCount}
    </Badge>
  )
})

CountBadge.displayName = 'CountBadge'

// Icon Badge - Badge with icon
interface IconBadgeProps extends Omit<BadgeProps, 'children'> {
  icon: React.ReactNode
  label: string
}

export const IconBadge = React.forwardRef<HTMLSpanElement, IconBadgeProps>(({
  icon,
  label,
  size = 'md',
  dot = false,
  ...props
}, ref) => {
  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  return (
    <Badge
      ref={ref}
      size={size}
      dot={dot}
      {...props}
    >
      <span className={iconSize[size]}>{icon}</span>
      {label}
    </Badge>
  )
})

IconBadge.displayName = 'IconBadge'

export default Badge
